import Collapse from './helpers/AccessibleCollapse';
import React, {useId, useRef, useState} from 'react';
import {Button, Modal, theme} from 'antd';
import {QuestionCircleOutlined} from '@ant-design/icons';
import Content from './Content';
import useCurrentOrg from './helpers/useCurrentOrg';
import {useTranslation} from 'react-i18next';

// Interactive help needs a focus-trapped dialog, not a hover portal at the end
// of the page's reading order. Keep the export for existing callers.
export default function HelpPopover({code, text, children, generalAndAdmin, title, id}) {
    const {t} = useTranslation();
    const currentOrg = useCurrentOrg();
    const {token} = theme.useToken();
    const [open, setOpen] = useState(false);
    const trigger = useRef();
    const content = useRef();
    const dialogId = useId();
    const stopKeys = event => event.stopPropagation();
    const helpTitle = t('help-info', 'Help Info');

    return <span onKeyDown={stopKeys} onKeyUp={stopKeys} onKeyPress={stopKeys} onClick={event => event.stopPropagation()}>
        <Button
            ref={trigger} id={id}
            type="text" shape="circle" size="small" icon={<QuestionCircleOutlined/>}
            style={{marginLeft: '.5em', color: token.colorTextSecondary}}
            title={helpTitle}
            aria-label={code ? t(`help-popover-${code}`, 'Help content for {{topic}}', {topic: code.replaceAll('-', ' ')}) : helpTitle}
            aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? dialogId : undefined}
            onClick={event => { event.preventDefault(); event.stopPropagation(); setOpen(true); }}
        >{title}</Button>
        <Modal
            open={open} title={<span id={`${dialogId}-title`}><QuestionCircleOutlined aria-hidden="true"/> {helpTitle}</span>}
            zIndex={10000} width={640}
            onCancel={() => setOpen(false)}
            afterOpenChange={visible => { if(visible) content.current?.focus(); }}
            afterClose={() => trigger.current?.focus()}
            footer={<Button onClick={() => setOpen(false)}>{t('close', 'Close')}</Button>}
        >
            <div id={dialogId} ref={content} tabIndex={-1} role="document" aria-label={helpTitle} style={{overflowWrap: 'anywhere'}}>
                {code ? (generalAndAdmin ? <Collapse defaultActiveKey={['general', 'admin']} collapsible="header">
                    <Collapse.Panel header={t('general', 'General')} key="general"><Content code={`${code}-general`}/></Collapse.Panel>
                    {currentOrg?.role_level && currentOrg.role_level !== 'general' && <Collapse.Panel header={t('admin', 'Admin')} key="admin"><Content code={`${code}-admin`}/></Collapse.Panel>}
                </Collapse> : <Content code={code}/>) : text || children}
            </div>
        </Modal>
    </span>;
}
